import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/home.css';
import Photo from './Photo';
import photos from '../asset/photos';
import certificationLogos from '../asset/certifications';
import { useContent, useLocalizePath } from '../i18n';

// Photo and link for each "Explore our range" card; the text is in i18n/<lang>/home.js
const featureTargets = [
  { photo: photos.threeBundles, link: '/products' },
  { photo: photos.grading, link: '/uses' },
  { photo: photos.peeling, link: '/true-cinnamon' },
];

// Icons for the "Why Spice Yield" cards, in the same order as the text
const whyIcons = [
  <path key="shield" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
  <React.Fragment key="globe">
    <circle cx="12" cy="12" r="10"/>
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </React.Fragment>,
  <path key="layers" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>,
  <path key="heart" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>,
];

function Home() {
  const t = useContent('home');
  const { certifications } = useContent('common');
  const lp = useLocalizePath();
  const features = t.range.features.map((f, i) => ({ ...f, ...featureTargets[i], link: lp(featureTargets[i].link) }));

  return (
    <div className="home">
      {/* Products Section */}
      <section className="range-section">
        <div className="container">
          <div className="range-header">
            <span className="section-eyebrow">{t.range.eyebrow}</span>
            <h2 className="range-heading">{t.range.heading}</h2>
          </div>
          <div className="range-grid">
            {features.map((feature, index) => (
              <article 
                key={feature.link} 
                className="range-card"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="range-image-wrapper">
                  <Photo
                    photo={feature.photo}
                    className="range-image"
                    sizes="(max-width: 768px) 120px, 33vw"
                  />
                  <div className="range-overlay">
                    <Link to={feature.link} className="range-link" tabIndex={-1} aria-hidden="true">
                      {feature.linkText}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </Link>
                  </div>
                </div>
                <div className="range-content">
                  <h3 className="range-title">
                    {/* Stretched link: the whole card is tappable, not just the hover button */}
                    <Link to={feature.link} className="range-card-link">{feature.title}</Link>
                  </h3>
                  <p className="range-description">{feature.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - Split Layout */}
      <section className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <span className="section-eyebrow">{t.about.eyebrow}</span>
              <h2 className="about-title">{t.about.title}</h2>
              <p className="about-text">{t.about.intro}</p>
              <p className="about-text">
                {t.about.knownAs.before}<em translate="no">Cinnamomum verum</em>{t.about.knownAs.after}
              </p>
              {/* Official marks as logos; the rest as text badges */}
              <ul className="about-cert-logos">
                {certifications.items.filter((c) => certificationLogos[c.name]).map((cert) => (
                  <li key={cert.name} className="cert-logo">
                    <span className="cert-logo-tile">
                      <img src={certificationLogos[cert.name]} alt={cert.name} loading="lazy" decoding="async" />
                    </span>
                    <span className="cert-logo-desc">{cert.desc}</span>
                  </li>
                ))}
              </ul>
              <div className="about-certs">
                {certifications.items.filter((c) => !certificationLogos[c.name]).map((cert) => (
                  <div key={cert.name} className="cert-badge">
                    <span className="cert-name" translate="no">{cert.name}</span>
                    <span className="cert-desc">{cert.desc}</span>
                  </div>
                ))}
              </div>
              <Link to={lp('/true-cinnamon')} className="about-link">
                {t.about.link}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </div>
            <div className="about-visual">
              <div className="about-image-wrapper">
                <Photo
                  photo={photos.estateTrees}
                  className="about-image"
                  sizes="(max-width: 1024px) 100vw, 45vw"
                />
                <div className="about-image-accent"></div>
              </div>
              <div className="about-badge">
                <span className="badge-number">30+</span>
                <span className="badge-text">{t.about.badgeText}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-section">
        <div className="container">
          <div className="why-header">
            <span className="section-eyebrow">{t.why.eyebrow}</span>
            <h2 className="why-title">{t.why.title}</h2>
            <p className="why-subtitle">{t.why.subtitle}</p>
          </div>
          <div className="why-grid">
            {t.why.cards.map((card, i) => (
              <div className="why-card" key={card.title}>
                <div className="why-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    {whyIcons[i]}
                  </svg>
                </div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-inner">
            <div className="stats-header">
              <span className="stats-eyebrow">{t.stats.eyebrow}</span>
              <h2 className="stats-title">{t.stats.title}</h2>
            </div>
            <div className="stats-grid">
              {t.stats.items.map((stat, index) => (
                <div key={index} className="stat-card">
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                  <span className="stat-suffix">{stat.suffix}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-grid">
            <div className="cta-content">
              <span className="section-eyebrow">{t.cta.eyebrow}</span>
              <h2 className="cta-title">{t.cta.title}</h2>
              <p className="cta-text">{t.cta.text}</p>
              <div className="cta-buttons">
                <Link to={lp('/contact')} className="cta-button primary">{t.cta.primary}</Link>
                <Link to={lp('/products')} className="cta-button secondary">{t.cta.secondary}</Link>
              </div>
            </div>
            <div className="cta-visual">
              <div className="cta-stat">
                <span className="cta-stat-number">50+</span>
                <span className="cta-stat-text">{t.cta.statText}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
